import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, GitCompare, Wand2, FileText, Mic, BarChart3, ArrowRight, Briefcase, Sparkles, BookOpen } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ScoreBars } from "@/components/ScoreBars";
import { ReadinessPanel } from "@/components/ReadinessPanel";

const tools = [
  { to: "/app/notebook", icon: BookOpen, title: "Career Notebook", desc: "Private notes, advice and reflections. Always free.", plus: false, hot: true },
  { to: "/app/upskill", icon: Sparkles, title: "AI Upskill Lab", desc: "Use AI to level up in your career: tools, prompts, 7-day plan", plus: false, hot: true },
  { to: "/app/compare", icon: GitCompare, title: "Compare Careers", desc: "See two careers side by side", plus: false },
  { to: "/app/simulator", icon: Wand2, title: "Career Simulator", desc: "Run 'What if?' scenarios", plus: false, soon: true },
  { to: "/app/report", icon: BarChart3, title: "Monthly Report", desc: "Your progress, coached", plus: false },
];

export default function Career() {
  const [dna, setDna] = useState(null);
  const [pro, setPro] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlus = user?.plan === "plus" || ["admin", "super_admin"].includes(user?.role);

  useEffect(() => {
    api.get("/dna").then(({ data }) => setDna(data)).catch(() => {});
    api.get("/pro/previews").then(({ data }) => setPro(data.features)).catch(() => {});
  }, []);

  if (!dna) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;
  const arch = dna.archetype;

  return (
    <div className="space-y-5">
      <h1 className="font-display text-3xl font-extrabold tracking-tight">Your Career</h1>

      <ReadinessPanel />

      <div className={`overflow-hidden rounded-3xl bg-gradient-to-br ${arch.color} p-6 text-white`} data-testid="career-archetype">
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/60">Career DNA</div>
        <h2 className="font-display text-2xl font-extrabold">{arch.name}</h2>
        <p className="mt-1 text-sm text-white/80">{arch.tagline}</p>
      </div>

      <div className="rounded-3xl border border-border bg-card p-6">
        <h3 className="mb-4 font-display text-lg font-bold">Dimension scores</h3>
        <ScoreBars dimensions={dna.result_dimensions} />
      </div>

      <div className="rounded-3xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">Career matches</h3>
          <span className="text-xs text-muted-foreground">{dna.is_plus ? "Top 15" : "Top 3 (free)"}</span>
        </div>
        <div className="space-y-2.5">
          {dna.matches.map((m, i) => (
            <div key={m.key} className="flex items-center gap-3 rounded-2xl border border-border p-3" data-testid={`career-match-${m.key}`}>
              <span className="w-5 text-center font-display font-bold text-muted-foreground">{i + 1}</span>
              <div className="flex-1"><div className="font-bold">{m.name}</div><div className="text-xs text-muted-foreground">{m.salary_mid}</div></div>
              <span className="font-mono text-lg font-extrabold text-indigo-600">{m.compatibility}%</span>
            </div>
          ))}
          {dna.locked_matches > 0 && (
            <button onClick={() => navigate("/paywall")} data-testid="career-unlock-btn"
              className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-indigo-200 bg-indigo-50/50 p-3.5 text-sm font-bold text-indigo-700 hover:bg-indigo-50">
              <Lock className="h-4 w-4" /> Unlock {dna.locked_matches} more matches
            </button>
          )}
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">{dna.salary_note}</p>
      </div>

      <div>
        <h3 className="mb-3 font-display text-lg font-bold">Career tools</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          {tools.map((t) => (
            <button key={t.to} onClick={() => (t.plus && !isPlus ? navigate("/paywall") : navigate(t.to))}
              data-testid={`tool-${t.to.split("/").pop()}`}
              className="group flex items-center gap-3 rounded-3xl border border-border bg-card p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-indigo-600/10 text-indigo-600"><t.icon className="h-5 w-5" /></div>
              <div className="flex-1">
                <div className="flex items-center gap-1.5 font-display font-bold">{t.title}{t.plus && !isPlus && <Lock className="h-3.5 w-3.5 text-amber-500" />}{t.soon && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-800">Soon</span>}{t.hot && <span className="rounded-full bg-indigo-600 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">New</span>}</div>
                <div className="text-xs text-muted-foreground">{t.desc}</div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
            </button>
          ))}
        </div>
      </div>
      <section className="space-y-3" data-testid="career-pro-previews"><h2 className="font-display text-lg font-bold">Pro career tools</h2><p className="text-sm text-muted-foreground">CV Rebuild, Negotiation Playbooks and Mock Interviews work for active Pro or complimentary accounts. Explore readable previews of every tool; check current subscription availability on the Pro page.</p>
        {user?.pro_access && <button data-testid="career-open-interview" onClick={() => navigate("/app/interview")} className="rounded-full bg-slate-900 px-5 py-3 text-sm font-bold text-white">Open my interview practice</button>}
        {pro.map(f => <button data-testid={`career-pro-${f.key}`} key={f.key} onClick={() => navigate(user?.pro_access && ({ cv: "/app/resume", negotiation: "/app/negotiation", interview: "/app/interview" })[f.key] || `/pro?feature=${f.key}`)} className="block w-full rounded-2xl border border-border bg-card p-5 text-left transition-colors hover:bg-secondary"><h3 className="font-display font-bold">{f.title}</h3><p className="mt-1 text-sm text-muted-foreground">{f.personalisation}</p><p className="mt-3 rounded-xl bg-slate-50 p-3 text-xs leading-relaxed text-slate-600">Sample: {f.sample.split("\n\n")[0]}</p><span className="mt-3 block text-xs font-bold text-indigo-700">{user?.pro_access && ["cv", "negotiation", "interview"].includes(f.key) ? "Open my tool — included in your access" : ["cv", "negotiation", "interview"].includes(f.key) ? "Included with Pro — ₹199/month" : "Preview only — not included in the current paid subscription"}</span></button>)}
      </section>
    </div>
  );
}
